import { useMemo, useRef, useState } from "react";
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import ptBR from "date-fns/locale/pt-BR";
import LocalStorage from "../../helpers/localstorage";
import GuidedTutorial from "../common/GuidedTutorial";

export interface AgendaDoc {
  id: number;
  name: string | null;
  data_vencimento: string;
  management_user_uploaddoc_access?: { management_user?: { name: string } }[];
}

interface AgendaWorkspaceProps {
  docs: AgendaDoc[];
  loading: boolean;
}

type CalendarEvent = {
  id: string;
  title: string;
  day: Date;
  color: string;
  dueDateLabel: string;
  signersLabel: string;
};

const WEEKDAY_LABELS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function toDateOnly(dateString: string): Date {
  const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const year = Number(match[1]);
    const month = Number(match[2]) - 1;
    const day = Number(match[3]);
    return new Date(year, month, day);
  }
  const parsed = parseISO(dateString);
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

export default function AgendaWorkspace({ docs, loading }: AgendaWorkspaceProps) {
  const [monthAnchor, setMonthAnchor] = useState(new Date());
  const [detailsDate, setDetailsDate] = useState<Date | null>(null);
  const localStorage = new LocalStorage();
  const replayActive = window.localStorage.getItem("management_tutorial_replay") === "1";
  const toolbarRef = useRef<HTMLDivElement>(null);
  const filterRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const events = useMemo<CalendarEvent[]>(
    () =>
      docs.map((doc) => {
        const dueDate = toDateOnly(doc.data_vencimento);
        const signers =
          doc.management_user_uploaddoc_access
            ?.map((u) => u.management_user?.name)
            .filter(Boolean)
            .join(", ") || "—";

        return {
          id: `contract-${doc.id}`,
          title: doc.name || "Contrato sem nome",
          day: dueDate,
          color: "#5DADE2",
          dueDateLabel: format(dueDate, "dd/MM/yyyy"),
          signersLabel: signers,
        };
      }),
    [docs]
  );

  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    events.forEach((event) => {
      const key = format(event.day, "yyyy-MM-dd");
      if (!map.has(key)) map.set(key, []);
      map.get(key)?.push(event);
    });
    return map;
  }, [events]);

  const monthDays = useMemo(() => {
    const monthStart = startOfMonth(monthAnchor);
    const gridStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const gridEnd = endOfWeek(endOfMonth(monthAnchor), { weekStartsOn: 0 });

    const days: Date[] = [];
    let current = gridStart;
    while (current <= gridEnd) {
      days.push(current);
      current = addDays(current, 1);
    }
    return days;
  }, [monthAnchor]);

  const monthInputValue = format(monthAnchor, "yyyy-MM");
  const detailsEvents = useMemo(() => {
    if (!detailsDate) return [];
    const key = format(detailsDate, "yyyy-MM-dd");
    return eventsByDate.get(key) || [];
  }, [detailsDate, eventsByDate]);

  const handleMonthFilter = (value: string) => {
    if (!value) return;
    const [year, month] = value.split("-").map(Number);
    if (!year || !month) return;
    setMonthAnchor(new Date(year, month - 1, 1));
  };

  return (
    <div className="agenda-pro-wrapper">
      <GuidedTutorial
        storageKey="management_tutorial_agenda_v1"
        enabled={replayActive && localStorage.getStep() === "7"}
        clearReplayOnFinish
        markTutorialCompleted
        steps={[
          { targetRef: toolbarRef as any, title: "Navegação da agenda", description: "Use os controles para avançar, voltar ou retornar ao mês atual." },
          { targetRef: filterRef as any, title: "Filtrar mês", description: "Selecione um mês específico para visualizar os vencimentos daquele período." },
          { targetRef: gridRef as any, title: "Calendário de vencimentos", description: "Cada card mostra contratos com vencimento no dia. Clique para abrir os detalhes." },
        ]}
      />
      <div ref={toolbarRef} className="agenda-pro-toolbar">
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-sm btn-outline-secondary" onClick={() => setMonthAnchor((prev) => subMonths(prev, 1))}>
            ◀
          </button>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => setMonthAnchor((prev) => addMonths(prev, 1))}>
            ▶
          </button>
          <button className="btn btn-sm modern-primary-btn" onClick={() => setMonthAnchor(new Date())}>
            Hoje
          </button>
          <h5 className="mb-0 ms-2">{format(monthAnchor, "MMMM yyyy", { locale: ptBR })}</h5>
        </div>

        <div className="d-flex align-items-center gap-2">
          <label className="small text-muted mb-0">Filtrar mês</label>
          <input
            ref={filterRef}
            type="month"
            className="form-control form-control-sm"
            style={{ width: 170 }}
            value={monthInputValue}
            onChange={(e) => handleMonthFilter(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="p-3">Carregando agenda...</div>
      ) : (
        <div className="gcal-month-shell">
          <div className="gcal-weekdays">
            {WEEKDAY_LABELS.map((label) => (
              <div className="gcal-weekday" key={label}>
                {label}
              </div>
            ))}
          </div>

          <div ref={gridRef} className="gcal-month-grid gcal-month-grid-clean">
            {monthDays.map((day) => {
              const key = format(day, "yyyy-MM-dd");
              const dayEvents = eventsByDate.get(key) || [];

              return (
                <button
                  type="button"
                  key={key}
                  className={`gcal-day-cell ${!isSameMonth(day, monthAnchor) ? "is-muted" : ""}`}
                  onClick={() => setDetailsDate(day)}
                >
                  <div className="gcal-day-number">{format(day, "d")}</div>
                  <div className="gcal-day-events">
                    {dayEvents.slice(0, 2).map((event) => (
                      <div
                        key={event.id}
                        className="agenda-month-event-card"
                        style={{ borderLeftColor: event.color }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDetailsDate(day);
                        }}
                      >
                        <div>📝 {event.title}</div>
                        <div>📅 Vencimento: {event.dueDateLabel}</div>
                        <div>👤 Signatários: {event.signersLabel}</div>
                      </div>
                    ))}
                    {dayEvents.length > 2 && <span className="gcal-more">+{dayEvents.length - 2} mais</span>}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {detailsDate && (
        <div className="agenda-modal-backdrop" onClick={() => setDetailsDate(null)}>
          <div className="agenda-modal" onClick={(e) => e.stopPropagation()}>
            <div className="d-flex align-items-center justify-content-between mb-3">
              <h5 className="mb-0">Eventos de {format(detailsDate, "dd/MM/yyyy", { locale: ptBR })}</h5>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setDetailsDate(null)}>
                Fechar
              </button>
            </div>

            {detailsEvents.length === 0 ? (
              <div className="gcal-empty">Nenhum evento neste dia.</div>
            ) : (
              <div className="d-flex flex-column gap-2">
                {detailsEvents.map((event) => (
                  <div key={event.id} className="agenda-month-event-card" style={{ borderLeftColor: event.color }}>
                    <div>📝 {event.title}</div>
                    <div>📅 Vencimento: {event.dueDateLabel}</div>
                    <div>👤 Signatários: {event.signersLabel}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
